import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import service from '@/utils/fetch';

let initialized = false;
let fcmToken = null; // 保存 FCM token
let jpushRegistrationId = null; // 保存 JPush regId
let isHuaweiDevice = false; // 是否为华为设备

/**
 * 检查用户是否已同意隐私政策
 * 只有在用户明确同意后，才允许初始化推送 SDK（JPush / FCM）
 */
function hasPrivacyAgreement() {
  try {
    if (typeof localStorage === 'undefined') {
      return false;
    }
    const agreed = localStorage.getItem('privacyAgreementAgreed');
    return agreed === 'true';
  } catch (e) {
    console.warn('[Push] 检查隐私协议状态失败，将视为未同意:', e);
    return false;
  }
}

/**
 * 同步 JCore/JPush 合规开关到原生侧（SharedPreferences + JCollectionAuth）
 * 目的：
 * - 老用户可能已在 H5 localStorage 同意，但原生侧还没记录（首次升级后需要补写）
 * - 确保在 init JPush/FCM 之前，原生侧已经被设置为“已同意”
 */
function syncNativePrivacyConsent(agreed) {
  try {
    const val = !!agreed;
    // JPush: window.JPush.setAuth (jpush-phonegap-plugin clobbers "JPush")
    if (typeof window !== 'undefined' && window.JPush && typeof window.JPush.setAuth === 'function') {
      window.JPush.setAuth(val);
    }
    // JCore: window.JCore.setAuth (cordova-plugin-jcore)
    if (typeof window !== 'undefined' && window.JCore && typeof window.JCore.setAuth === 'function') {
      window.JCore.setAuth(val);
    }
  } catch (e) {
    console.warn('[Push] syncNativePrivacyConsent failed:', e);
  }
}

/**
 * 检测是否为华为设备
 */
function detectHuaweiDevice() {
  try {
    if (typeof navigator !== 'undefined' && navigator.userAgent) {
      const ua = navigator.userAgent.toLowerCase();
      // 检测华为设备标识
      return /huawei|honor|hmos/.test(ua) || 
             /huawei/i.test(navigator.userAgent) ||
             /honor/i.test(navigator.userAgent);
    }
  } catch (e) {
    console.warn('[Push] Error detecting Huawei device:', e);
  }
  return false;
}

/**
 * 检查用户是否已登录
 */
function isUserLoggedIn() {
  try {
    const userInfoStr = sessionStorage.getItem("userInfoXpz");
    if (userInfoStr) {
      const userInfo = JSON.parse(userInfoStr);
      return !!(userInfo && userInfo.access_token);
    }
  } catch (e) {
    console.error('[Push] Error checking login status:', e);
  }
  return false;
}

/**
 * 保存 token 到服务器
 * @param {string} token - 设备推送 token
 * @param {'fcm'|'jpush'} type - 推送通道类型
 */
export async function saveTokenToServer(token, type = 'fcm') {
  if (!token) {
    console.warn('[Push] No token provided to save');
    return;
  }

  // 检查用户是否已登录
  if (!isUserLoggedIn()) {
    console.log('[Push] User not logged in, token will be saved after login');
    return;
  }

  try {
    const baseURL = service.defaults.baseURL || 'https://xpzhp.com';
    const requestPath = '/api/account/jpush/save-token';
    const fullUrl = `${requestPath}?fcmToken=${encodeURIComponent(token)}&type=${encodeURIComponent(type)}`;
    
    console.log('[Push] 准备保存 token 到服务器');
    console.log('[Push] Full URL:', baseURL + fullUrl);
    
    const response = await service.post(fullUrl, null, {
      noLoading: true,
    });
    console.log('[Push] ========== Save Token Success ==========');
    console.log('[Push] Response Type:', typeof response);
    console.log('[Push] Response:', response);
    console.log('[Push] Response (JSON):', JSON.stringify(response, null, 2));
    if (response && typeof response === 'object') {
      console.log('[Push] Response Keys:', Object.keys(response));
      if (response.status !== undefined) {
        console.log('[Push] Response Status:', response.status);
      }
      if (response.msg !== undefined) {
        console.log('[Push] Response Message:', response.msg);
      }
      if (response.ret !== undefined) {
        console.log('[Push] Response Data (ret):', response.ret);
      }
    }
    console.log('[Push] =========================================');
    return response;
  } catch (e) {
    console.error('[Push] save token failed');
    console.error('[Push] error message:', e.message || 'Unknown error');
    
    if (e.response) {
      console.error('[Push] response status:', e.response.status);
      console.error('[Push] response statusText:', e.response.statusText);
      console.error('[Push] response data:', e.response.data);
    } else {
      console.error('[Push] No response object (可能是网络错误或CORS问题)');
    }
    
    if (e.config) {
      console.error('[Push] request URL:', e.config.baseURL + e.config.url);
      console.error('[Push] request method:', e.config.method);
    }
    
    throw e;
  }
}

/**
 * 初始化 JPush（厂商通道）
 */
async function initJPush() {
  // 未同意隐私协议时，不初始化 JPush，避免通过 SDK 获取任何用户信息
  if (!hasPrivacyAgreement()) {
    console.log('[Push] 用户未同意隐私协议，跳过 JPush 初始化');
    return;
  }

  // 仅原生且插件存在时执行
  if (!Capacitor.isNativePlatform()) return;
  const JPush = typeof window !== 'undefined' ? window.JPush : null;
  if (!JPush) {
    console.warn('[Push] JPush plugin not found', { windowJPush: typeof window !== 'undefined' ? window.JPush : null });
    return;
  }

  try {
    console.log('[Push] initJPush - starting initialization', { JPushObjectType: typeof JPush });
    JPush.init();
    JPush.setDebugMode && JPush.setDebugMode(true); // 调试可开

    // 获取注册 ID（立即尝试一次）
    const fetchRegId = async () => {
      try {
        console.log('[Push] fetchRegId - calling JPush.getRegistrationID');
        JPush.getRegistrationID(async (id) => {
          console.log('[Push] getRegistrationID callback invoked', { id });
          if (id) {
            console.log('[Push] JPush regId:', id);
            jpushRegistrationId = id;
            if (isUserLoggedIn()) {
              console.log('[Push] User is logged in, saving regId now');
              await saveTokenToServer(id, 'jpush');
            } else {
              console.log('[Push] User not logged in yet, will save regId after login');
            }
          } else {
            console.warn('[Push] JPush regId is empty, will retry later', { returnedId: id });
          }
        });
      } catch (e) {
        console.error('[Push] fetchRegId error:', e);
      }
    };
    await fetchRegId();

    // 监听 regId 下发事件，确保异步获取到也能保存
    JPush.addEventListener &&
      JPush.addEventListener('jpush.receiveRegistrationId', async (event) => {
        try {
          console.log('[Push] jpush.receiveRegistrationId event:', event);
          const id = event?.registrationId || event?.registrationID || event?.regId;
          console.log('[Push] parsed reg id from event:', id);
          if (id) {
            console.log('[Push] JPush receiveRegistrationId:', id);
            jpushRegistrationId = id;
            if (isUserLoggedIn()) {
              console.log('[Push] User logged in, saving JPush regId from event');
              await saveTokenToServer(id, 'jpush');
            } else {
              console.log('[Push] User not logged in, will save regId from event after login');
            }
          } else {
            console.warn('[Push] JPush receiveRegistrationId event without id:', event);
          }
        } catch (e) {
          console.error('[Push] Error handling jpush.receiveRegistrationId event:', e);
        }
      });

    // 监听通知
    JPush.addEventListener &&
      JPush.addEventListener('jpush.receiveNotification', (event) => {
        console.log('[Push] JPush receiveNotification:', event);
      });

    JPush.addEventListener &&
      JPush.addEventListener('jpush.openNotification', (event) => {
        console.log('[Push] JPush openNotification:', event);
        // TODO: 根据 event.extra 跳转
      });

    JPush.addEventListener &&
      JPush.addEventListener('jpush.receiveMessage', (event) => {
        console.log('[Push] JPush receiveMessage:', event);
      });
  } catch (err) {
    console.error('[Push] JPush init failed:', err);
  }
}

/**
 * 初始化 FCM（GMS 兜底）
 */
async function initFCM() {
  // 未同意隐私协议时，不初始化 FCM
  if (!hasPrivacyAgreement()) {
    console.log('[Push] 用户未同意隐私协议，跳过 FCM 初始化');
    return;
  }

  // 只在原生环境运行，避免 Web/PC 报错
  if (!Capacitor.isNativePlatform()) return;

  // 检测是否为华为设备
  isHuaweiDevice = detectHuaweiDevice();
  if (isHuaweiDevice) {
    console.log('[Push] 检测到华为设备，FCM 推送可能不可用（需要 Google Play Services）');
    console.log('[Push] 如需更好支持，依赖厂商通道/JPush');
  }

  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== 'granted') {
    console.log('[Push] Permission not granted');
    return;
  }

  // 设置超时，避免在无 GMS 设备无限等待
  const registerPromise = PushNotifications.register();
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => {
      if (isHuaweiDevice) {
        console.warn('[Push] FCM 注册超时，可能是华为设备无 GMS');
      }
      reject(new Error('Push registration timeout'));
    }, 10000); // 10秒超时
  });

  try {
    await Promise.race([registerPromise, timeoutPromise]);
  } catch (err) {
    if (err.message === 'Push registration timeout') {
      console.warn('[Push] FCM 注册超时，跳过 FCM 初始化');
      return;
    }
    throw err;
  }

  // 监听设备 token 注册成功
  PushNotifications.addListener('registration', async ({ value }) => {
    console.log('[Push] FCM token:', value);
    fcmToken = value;

    // 如果用户已登录，立即保存 token
    if (isUserLoggedIn()) {
      console.log('[Push] User already logged in, saving FCM token...');
      await saveTokenToServer(value, 'fcm');
    } else {
      console.log('[Push] User not logged in, FCM token saved locally, will save after login');
    }
  });

  PushNotifications.addListener('registrationError', (err) => {
    console.error('[Push] registrationError raw:', err);
    try {
      console.error(
        '[Push] registrationError json:',
        JSON.stringify(err, Object.getOwnPropertyNames(err || {}), 2)
      );
    } catch (e) {
      console.error('[Push] registrationError stringify failed:', e);
    }

    if (err?.message) console.error('[Push] registrationError message:', err.message);
    if (err?.error) console.error('[Push] registrationError error:', err.error);
    if (err?.code) console.error('[Push] registrationError code:', err.code);

    if (isHuaweiDevice) {
      console.warn('[Push] 华为设备 FCM 注册失败，这是正常的（无 GMS）');
    }
  });

  PushNotifications.addListener('pushNotificationReceived', (notif) => {
    console.log('[Push] received', notif);
  });

  PushNotifications.addListener('pushNotificationActionPerformed', (notif) => {
    console.log('[Push] action', notif);
  });
}

/**
 * 初始化推送服务（应用启动时调用）
 * 优先 JPush（厂商通道），FCM 作为兜底
 */
export async function initPush() {
  if (initialized) return;
  // 未同意隐私协议时，整个推送体系不初始化
  if (!hasPrivacyAgreement()) {
    console.log('[Push] 用户未同意隐私协议，跳过推送服务初始化');
    return;
  }
  if (!Capacitor.isNativePlatform()) return;
  initialized = true;

  // 确保原生侧（JCore/JPush）已收到“已同意”信号（并持久化），再做 SDK 初始化
  syncNativePrivacyConsent(true);

  // 并行启动 JPush（主通道）和 FCM（兜底）
  await initJPush();
  await initFCM();
}

/**
 * 在用户登录成功后调用此方法，保存 token 到服务器
 * 应该在登录页面登录成功后调用
 */
export async function onUserLoggedIn() {
  // 未同意隐私协议时，不进行任何与推送相关的操作（包括 token 上报）
  if (!hasPrivacyAgreement()) {
    console.log('[Push] 用户未同意隐私协议，登录后也不进行推送 token 绑定');
    return;
  }

  console.log('[Push] onUserLoggedIn called, current tokens:', { jpushRegistrationId, fcmToken });

  // 确保推送服务已初始化
  if (!initialized && Capacitor.isNativePlatform()) {
    console.log('[Push] Push service not initialized, initializing now...');
    try {
      await initPush();
    } catch (e) {
      console.warn('[Push] Failed to initialize push service:', e);
    }
  }

  // 先尝试保存 JPush regId
  if (jpushRegistrationId) {
    console.log('[Push] User logged in, saving JPush regId...');
    await saveTokenToServer(jpushRegistrationId, 'jpush');
  } else {
    console.log('[Push] No JPush regId yet, attempting to get it...');
    const JPush = typeof window !== 'undefined' ? window.JPush : null;
    if (JPush && Capacitor.isNativePlatform()) {
      // 使用 Promise 来等待获取 registrationId
      try {
        const regId = await new Promise((resolve) => {
          const timeout = setTimeout(() => {
            console.log('[Push] Timeout waiting for JPush regId');
            resolve(null);
          }, 5000); // 5秒超时

          JPush.getRegistrationID((id) => {
            clearTimeout(timeout);
            if (id) {
              console.log('[Push] Successfully fetched JPush regId after login:', id);
              jpushRegistrationId = id;
              resolve(id);
            } else {
              console.log('[Push] JPush regId still empty after login');
              resolve(null);
            }
          });
        });

        if (regId) {
          await saveTokenToServer(regId, 'jpush');
        }
      } catch (e) {
        console.warn('[Push] Error getting JPush regId:', e);
      }
    } else {
      console.log('[Push] JPush not available or not native platform');
    }
  }

  // 再尝试保存 FCM token（兜底）
  if (fcmToken) {
    console.log('[Push] User logged in, saving FCM token...');
    await saveTokenToServer(fcmToken, 'fcm');
  } else {
    console.log('[Push] No FCM token available yet, will save when received');
  }
}


